import * as Sentry from '@sentry/node';
import axios, { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Row, Toast } from 'react-bootstrap';
import FlatPickr from 'react-flatpickr';
import BarLoader from 'react-spinners/BarLoader';
import { useFranchiseeContext } from '../../context/Franchisee';
import { SCHEDULE, useLeadContext } from '../../context/Lead';
// import getHolidays from '../../SDKs/Gataware/getHolidays';
import db from '../../SDKs/firebase/firebase';
import { pink500 } from '../../ui/colors';
import BookingSummary from '../BookingSummary';
import ToastWrapper from '../ToastWrapper';
// import getFreeTimeBlocks from '../../SDKs/Gataware/partial/getFreeTimeBlocks';
// import setLeadCleaningTime from '../../SDKs/Gataware/partial/setLeadCleaningTime';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  setLockID,
  setSelectedTimeSlot,
} from '../../features/user/userSlice';
import formatPhoneNumber from '../PhoneLink/formatPhoneNumber';

interface BookingScheduleProps {
  // add Booking Schedule props here
}

type TimeBlock = {
  startTime: any;
  teamId: any;
};

type TimeBlockState = {
  morning?: TimeBlock;
  midday?: TimeBlock;
  afternoon?: TimeBlock;
};

const getNextBusinessDay = (): DateTime => {
  let defaultDate = DateTime.local().plus({ days: 1 }); // tomorrow
  if (defaultDate.weekday >= 6) {
    defaultDate = defaultDate.startOf('week').plus({ days: 7 });
  }
  return defaultDate;
};
const disableWeekends = (date) => date.getDay() === 0 || date.getDay() === 6;
// const disableDays = ({ saturday, sunday } = { saturday: false, sunday: false }) => (date) => {
//   const disableSaturday = saturday ? false : date.getDay() === 6;
//   const disableSunday = sunday ? false : date.getDay() === 0;

//   return (disableSaturday || disableSunday);
// };

const BookingSchedule: React.FC<BookingScheduleProps> = () => {
  const { lead, dispatchLead } = useLeadContext();
  const [cleaningDate, setCleaningDate] = useState<Date>(new Date());
  const [timeSelected, setTimeSelected] = useState<boolean>(false);
  const { franchisee } = useFranchiseeContext();
  // const { franchisee } = useFranchiseeContext();
  let onlineUserReference;
  const [holidays, setHolidays] = useState<Array<Function | string>>([
    DateTime.local().toISODate(),
    disableWeekends,
  ]);
  const [holidaysEnabled, setHolidaysEnabled] = useState<boolean>(false);

  const [errorToast, setErrorToast] = useState<boolean>(false);
  // const user = useSelector((state) => state?.['user']);
  const [flatpickrDate, setFlatpickrDate] = useState<string>('');
  const [franchiseeSlug, setFranchiseeSlug] = useState<string>(
    franchisee.slug.toUpperCase().replaceAll('-', '_')
  );

  const dispatch = useDispatch();

  const detectUserAction = () => {
    if (lead?.id === -1) return;
    try {
      onlineUserReference = db.ref(`/online/${lead?.id}`);
      onlineUserReference.update({ typing: true });

      setTimeout(() => {
        onlineUserReference.update({ typing: false });
      }, 500);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setFranchiseeSlug(franchisee.slug.toUpperCase().replaceAll('-', '_'));
  }, [franchisee]);

  useEffect(() => {
    // getHolidays({ franchiseeId: franchisee.id })
    // console.log('Getting holidays!');
    axios
      .post('/api/get-holidays/', { franchiseeId: franchisee.id })
      .then(({ data, status }: AxiosResponse) => {
        // console.log('Holidays', data);
        if ((status === 200 && data.length) || status === 204) {
          // console.log("Found holidays!")
          const newHolidays = data.map(({ date }) => date);
          setHolidaysEnabled(true);
          setHolidays([
            DateTime.local().toISODate(),
            disableWeekends,
            ...newHolidays,
          ]);
          // console.log(holidays);
        } else {
          // console.log("No holidays found.");
          if (data.message === 'aborted') {
            setHolidaysEnabled(true);
          }
        }
      })
      .catch((error) => {
        // console.log(error);
        setHolidaysEnabled(true);
        Sentry.captureException(error);
      });
  }, []);

  const [loading, setLoading] = useState<boolean>(false);
  const [memoDates, setMemoDateState] = useState<Function | Object>({});
  const [timeBlocks, setTimeBlocks] = useState<TimeBlockState>({});
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [sameDate, setSameDate] = useState<boolean>(false);
  const currentDate = new Date();
  const [morningAvailable, setMorningAvailable] = useState<boolean>(false);
  const [midDayAvailable, setMidDayAvailable] = useState<boolean>(false);
  const [afternoonAvailable, setAfternoonAvailable] = useState<boolean>(false);

  const [morningTeams, setMorningTeam] = useState([]);
  const [midDayTeams, setMiddayTeams] = useState([]);
  const [afternoonTeams, setAfternoonTeams] = useState([]);

  const firstRender = useRef<boolean>(false);
  const secondRender = useRef<boolean>(false);
  const hitApiOnce = useRef<boolean>(false);

  const morningTimes = ['480', '510', '540', '570', '600', '630'];
  const midDayTimes = ['660', '690', '720', '750', '780', '810'];
  const afternoonTimes = ['840', '870', '900', '930', '960', '990', '1020'];

  const setMemoDate = useCallback(
    (data: {}) => {
      setMemoDateState((prev: Object) => ({
        ...prev,
        [cleaningDate.toLocaleString()]: data,
      }));
    },
    [cleaningDate]
  );

  const fpRef = useRef(null);

  useEffect(() => {
    const instance = fpRef.current?.flatpickr;
    if (!instance) return;

    const patchMonthSelect = () => {
      const monthSelect = instance.monthNav?.querySelector(
        ".flatpickr-monthDropdown-months"
      );
      if (monthSelect) {
        monthSelect.tabIndex = 0;
        monthSelect.removeAttribute("aria-hidden");
        monthSelect.setAttribute("aria-label", "Select month");

        // Let arrow keys work natively
        monthSelect.addEventListener(
          "keydown",
          (e) => {
            if (["ArrowDown", "ArrowUp", "Home", "End", " "].includes(e.key)) {
              e.stopPropagation();
            }
          },
          { capture: true }
        );
      }
    };
    patchMonthSelect();
    const observer = new MutationObserver(() => patchMonthSelect());
    if (instance.monthNav) {
      observer.observe(instance.monthNav, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, []);

  const getMemoDate = (date) => memoDates[date.toLocaleString()];
  
  const submitCleaningDate = ({ teamId, startTime }: TimeBlock) => {
    // console.log("The team ID is: ", teamId)
    // console.log("The start time is: ", startTime)
    let requestData = {
      leadId: lead.id,
      teamId: teamId,
      selectedTime: startTime,
      date: DateTime.fromJSDate(cleaningDate).toLocaleString(),
    };

    // setLeadCleaningTime(requestData)
    axios
      .post('/api/set-lead-appointment-time/', requestData)
      .then(({ data, status }) => {
        // console.log("The set lead appointment data is: ", data)
        if (data.status === 409) {
          setErrorToast(true);
          setSelectedTime('');
          setErrorMessage(
            'This time slot has just been booked. Please choose another time slot.'
          );
          let resetLeadDateTime = data.data.data;
          resetLeadDateTime.dateTime = null;
          dispatchLead({ type: SCHEDULE, payload: resetLeadDateTime }); // there might be a lot of these based on how we've handled the lead dispatch
          Sentry.captureMessage(data?.data?.message);
          return;
        }
        switch (status) {
          case 200:
            setErrorToast(false);
            dispatchLead({ type: SCHEDULE, payload: data.data.data }); // there might be a lot of these based on how we've handled the lead dispatch
            try {
              db.ref(
                process.env.kpiDashboardCollectionName + '/' + lead?.id
              ).update({
                teamId: teamId,
                selectedTime: startTime,
                date: DateTime.fromJSDate(cleaningDate).toLocaleString(),
                step: 5,
              });
            } catch (error) {
              console.log(error);
            }
            try {
              const userStatusDatabaseRef = db.ref(`/online/${lead?.id}`);

              db.ref('.info/connected').on('value', async (snap) => {
                if (snap.val() === true) {
                  await userStatusDatabaseRef.onDisconnect().remove();
                  await userStatusDatabaseRef.set({
                    leadID: lead?.id,
                  });
                } else {
                  await userStatusDatabaseRef.remove();
                }
              });
            } catch (error) {
              console.log(error);
            }
            try {
              const userStatusDatabaseRef = db.ref(`/online/${lead?.id}`);

              db.ref('.info/connected').on('value', async (snap) => {
                if (snap.val() === true) {
                  await userStatusDatabaseRef.onDisconnect().remove();
                  await userStatusDatabaseRef.set({
                    leadID: lead?.id,
                  });
                } else {
                  await userStatusDatabaseRef.remove();
                }
              });
            } catch (error) {
              console.log(error);
            }
            break;
          case 400:
          default:
            break;
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        // console.log('Error setting cleaning date', error);
      });
    detectUserAction();
  };

  useEffect(() => {
    if (lead.id === -1) {
      firstRender.current = true;
      return;
    }
    if (lead.id !== -1) {
      secondRender.current = true;
    }
  }, [lead]);

  const requestTimeBlocks = useCallback(() => {
    if (lead.id === -1) return;
    setLoading(true);

    let requestData = {
      leadId: lead.id,
      date: cleaningDate.toLocaleString(),
    };
    // getFreeTimeBlocks(requestData)
    axios
      .post('/api/get-free-time-blocks/', requestData)
      .then(({ data, status }: AxiosResponse) => {
        // console.log("The free time blocks data is: ", data.data);
        switch (status) {
          case 200:
            // console.log("Success!");
            // console.log(data.message, { data });
            // setMemoDate(data.data);
            setTimeBlocks(data.data);
            break;
          case 400:
            setErrorToast(true);
            setTimeBlocks({});
            setErrorMessage(data?.data?.message);
            Sentry.captureException(data?.data?.message);
            break;
          default:
            // console.log(data.message, { data });
            // console.log("Inside default")
            setTimeBlocks({});
            break;
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        // console.log({ error });
        setTimeBlocks({});
      })
      .finally(() => {
        setLoading(false);
        // console.log("The timeblocks are: ", timeBlocks);
      });
  }, [cleaningDate, lead]);

  useEffect(() => {
    if (cleaningDate) {
      // console.log(cleaningDate);
      if (getMemoDate(cleaningDate)) {
        // console.log('Record Found', getMemoDate(cleaningDate));
        setTimeBlocks(getMemoDate(cleaningDate));
      } else {
        requestTimeBlocks();
      }
    }
  }, [cleaningDate]);

  useEffect(() => {
    const currentFullDate = `${currentDate.getDate()} ${currentDate.getMonth()} ${currentDate.getFullYear()}`;
    const cleaningFullDate = `${cleaningDate.getDate()} ${cleaningDate.getMonth()} ${cleaningDate.getFullYear()}`;

    if (currentFullDate === cleaningFullDate) {
      setSameDate(true);
    } else {
      setSameDate(false);
    }
  }, [cleaningDate]);
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return (
    <div id='booking-form-schedule'>
      <Row>
        <Col xs={12} md={6}>
          <h5>Choose Your Day</h5>
          <FlatPickr
            onChange={async (e) => {
              setTimeSelected(true);
              setFlatpickrDate(e[0].toLocaleDateString('en-US', options));
              const [date] = e;
              const formatedDate = moment(e[0]).format('YYYYMMDD');
              setCleaningDate(date);
              setSelectedTime('');
              // setCleaningDate(e)
            }}
            // defaultValue={new Date().toLocaleDateString()}
            className='book__datepicker flatpickr'
            ref={fpRef}
            options={{
              monthSelectorType: "dropdown",
              onReady: (dates, dateStr, instance) => {
                // Month and year selects
                const monthSelect = instance.monthNav.querySelector(".flatpickr-monthDropdown-months");
                console.log('Instance monthNav', monthSelect)
                const yearInput = instance.yearElements?.[0];

                if (monthSelect) {
                  monthSelect.setAttribute("tabindex", "0");
                  monthSelect.setAttribute("aria-label", "Select month");
                }
                if (yearInput) {
                  yearInput.setAttribute("tabindex", "0");
                  yearInput.setAttribute("aria-label", "Select year");
                }
              },
              onDayCreate: (dObj, dStr, fp, dayElem) => {
                if (dayElem.classList.contains("flatpickr-disabled")) {
                  dayElem.removeAttribute("tabindex"); // don’t tab into disabled days
                } else {
                  dayElem.tabIndex = 0; // allow tab focus
                }
              },
              static: true,
              clickOpen: false,
              inline: true,
              dateFormat: 'Y-m-d',
              mode: 'single',
              minDate: DateTime.local().toISODate(),
              maxDate: new Date(
                new Date().setFullYear(new Date().getFullYear() + 2)
              ),
              defaultDate: getNextBusinessDay().toISODate(),
              locale: {
                firstDayOfWeek: 0,
                lastDayOfWeek: 5,
              },
              disable: holidaysEnabled
                ? holidays
                : [
                    {
                      from: '2000-04-01',
                      to: '2025-05-01',
                    },
                  ],
              }}
          />
        </Col>
        <Col xs={12} md={6} className='mt-5 mt-md-0'>
          {timeSelected && <h5>Choose Your Time</h5>}
          {cleaningDate && <hr />}
          {cleaningDate && (
            <h6 className='schedule-selected-date mt-4 mb-4'>
              {DateTime.fromJSDate(cleaningDate).toFormat('DDDD')}
            </h6>
          )}
          {cleaningDate && <hr />}
          {timeSelected && (
            <p className='mb-2 estimated-arrival'>*Preferred Arrival Window</p>
          )}
          <Row className='justify-content-center m1-3' style={{ minHeight: 4 }}>
            <BarLoader loading={loading} color={pink500} />
          </Row>
          <div className='book__timepicker '>
            
            {timeBlocks.morning && !loading && (
              <Button
                variant='primary'
                disabled={loading}
                className='btn-block mb-2'
                style={{
                  color: selectedTime === 'morning' ? '#fff' : '',
                  backgroundColor: selectedTime === 'morning' ? '#e02d88' : '',
                  borderColor: selectedTime == 'morning' ? '#D4357F' : '',
                }}
                onClick={() => {
                  submitCleaningDate(timeBlocks.morning);
                  setSelectedTime('morning');
                }}
                tabIndex={0}
              >
                Morning (8am-11am)
              </Button>
            )}
            
            {timeBlocks.midday && !loading && (
              <Button
                variant='primary'
                disabled={loading}
                className='btn-block mb-2'
                style={{
                  color: selectedTime === 'midday' ? '#fff' : '',
                  backgroundColor: selectedTime === 'midday' ? '#e02d88' : '',
                  borderColor: selectedTime == 'midday' ? '#D4357F' : '',
                }}
                onClick={() => {
                  submitCleaningDate(timeBlocks.midday);
                  setSelectedTime('midday');
                }}
                tabIndex={0}
              >
                Midday (11am-2pm)
              </Button>
            )}
            
            {timeBlocks.afternoon && !loading && (
              <Button
                variant='primary'
                disabled={loading}
                className='btn-block mb-2'
                style={{
                  color: selectedTime === 'afternoon' ? '#fff' : '',
                  backgroundColor:
                    selectedTime === 'afternoon' ? '#e02d88' : '',
                  borderColor: selectedTime == 'afternoon' ? '#D4357F' : '',
                }}
                onClick={() => {
                  submitCleaningDate(timeBlocks.afternoon);
                  setSelectedTime('afternoon');
                }}
                tabIndex={0}
              >
                Afternoon (2pm-5pm)
              </Button>
            )}
            {cleaningDate && <hr style={{marginBottom: '0'}} />}
            {timeSelected && !loading && (
              <p className='booking-terms-disclaimer' style={{ fontSize: '14px'}}>
                *Your scheduled appointment time may vary from your selection. Please text or call us <a href={`tel:${franchisee.phonePrimary}`}>{formatPhoneNumber(franchisee?.phonePrimary)}</a> if there are specific limitations to your schedule.
              </p>
            )}

            {/* <GridLoader className="mx-auto" color={pink500} loading={loading} /> */}
          </div>
        </Col>
      </Row>

      <BookingSummary errorToast={errorToast} />

      <ToastWrapper>
        <Toast
          show={errorToast}
          onClose={() => setErrorToast(false)}
          delay={4000}
          autohide
        >
          <Toast.Body className='toast-error' style={{ background: '#faae05' }}>
            {errorMessage}
          </Toast.Body>
        </Toast>
      </ToastWrapper>
    </div>
  );
};

export default BookingSchedule;
